/* eslint-disable @typescript-eslint/no-explicit-any */
import EventEmitter from 'events';
import { LoggerEventListenerEnum, LoggerLevelEnum } from './logger.enum';
import { LogEntry, LogInfoExtraction } from './logger.interface';

/**
 * Source : https://adrianhall.github.io/cloud/2019/06/30/building-an-efficient-logger-in-typescript/
 */
export class Logger {
  private logManager: EventEmitter;
  private minLevel: LoggerLevelEnum;
  private fromServiceWorker: boolean;

  constructor(
    logManager: EventEmitter,
    minLevel: LoggerLevelEnum,
    fromServiceWorker: boolean,
  ) {
    this.logManager = logManager;
    this.minLevel = minLevel;
    this.fromServiceWorker = fromServiceWorker;
  }

  /**
   * Get location of current method
   */
  private getCurrentScriptLocation(): string | undefined {
    let location: string | undefined = undefined;
    // Obtain the line/file through a thoroughly hacky method
    // This creates a new stack trace and pulls the caller from it.  If the caller
    // if .trace()
    const error = new Error('');
    if (error.stack) {
      const cla = error.stack.split('\n');
      let idx = 1;
      while (idx < cla.length && cla[idx].includes('at Logger.Object.')) idx++;
      if (idx < cla.length) {
        location = cla[idx].slice(cla[idx].indexOf('at ') + 3, cla[idx].length);

        // Removes info inside parenthesis
        if (location.indexOf('(') > 0) {
          location = location.substring(0, location.indexOf('(') - 1);
        }
      }
    }

    return location;
  }

  /**
   * Get location of current script execution
   */
  private getLoc(e?: Error): string | undefined {
    let location: string | undefined = undefined;

    const arraySuffix: Array<string> = ['vue?', 'vue:', 'ts?', 'ts:'];

    const error = e || new Error('');
    if (error.stack) {
      const cla = error.stack.split('\n');
      const stackLine: string = cla.length > 4 ? cla[5] : '';

      arraySuffix.forEach((s) => {
        if (!location) {
          const index = stackLine.lastIndexOf(s);
          if (index > 0) {
            location = stackLine.substring(0, index + (s.length - 1));

            const idxSlash = location.lastIndexOf('/');
            if (idxSlash > 0) {
              location = location.substring(idxSlash + 1);
            }
          }
        }
      });
    }

    if (!location) {
      location = this.getCurrentScriptLocation();
    }

    return location;
  }

  /**
   * Parse the message string to get : title, message and location
   * Title is between []
   * Locatiojn is between {}
   * @param str
   * @returns
   */
  private getInformationsFromString(
    str: string,
  ): LogInfoExtraction | undefined {
    let title: string | undefined = undefined;
    let message: string | undefined = undefined;
    let location: string | undefined = undefined;

    let tempString = str;

    if (typeof tempString === 'string') {
      // Parse Title
      const idxBracketStart = tempString.indexOf('[');
      const idxBracketEnd = tempString.indexOf(']');
      if (
        idxBracketStart >= 0 &&
        idxBracketEnd >= 0 &&
        idxBracketStart < idxBracketEnd
      ) {
        title = tempString.substring(idxBracketStart + 1, idxBracketEnd);
        tempString = tempString.replace(`[${title}]`, '');
      }

      // Parse Location
      const idxBraceStart = tempString.indexOf('{');
      const idxBraceEnd = tempString.indexOf('}');
      if (
        idxBraceStart >= 0 &&
        idxBraceEnd >= 0 &&
        idxBraceStart < idxBraceEnd
      ) {
        location = tempString.substring(idxBraceStart + 1, idxBraceEnd);
        tempString = tempString.replace(`{${location}}`, '');
      }

      if (!location) location = this.getLoc();

      // Parse message
      message = tempString.trim();
    }

    return {
      title,
      message,
      location,
    };
  }

  private populateLogEntry(logEntry: LogEntry): LogEntry {
    // Set title and content if needed
    if (
      logEntry.arguments.length > 0 &&
      typeof logEntry.arguments[0] === 'string'
    ) {
      const extract = this.getInformationsFromString(logEntry.arguments[0]);
      logEntry.title = extract?.title;
      logEntry.message = extract?.message;
      logEntry.location = extract?.location;
      logEntry.timestamp = new Date().getTime();
    }

    return logEntry;
  }

  /**
   * Central logging method.
   * @param logLevel
   * @param message
   */
  public doLog(
    fromServiceWorker: boolean,
    logLevel: LoggerLevelEnum,
    ..._arguments: any[]
  ): void {
    if (logLevel < this.minLevel) return;

    // Init logEntry
    const logEntry: LogEntry = {
      level: logLevel,
      arguments: _arguments,
      fromServiceWorker,
    };

    this.doConsoleLogFromLogEntry(logEntry);
  }

  public doConsoleLogFromLogEntry(logEntry: LogEntry) {
    if (logEntry.level < this.minLevel) return;
    this.logManager.emit(
      LoggerEventListenerEnum.CONSOLE_LOG,
      this.populateLogEntry(logEntry),
    );
  }

  public trace(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.TRACE, ...arg);
  }
  public debug(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.DEBUG, ...arg);
  }
  public info(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.INFO, ...arg);
  }
  public log(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.LOG, ...arg);
  }
  public warn(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.WARN, ...arg);
  }
  public error(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.ERROR, ...arg);
  }
  public fatal(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.FATAL, ...arg);
  }
  public display(...arg: any[]): void {
    this.doLog(this.fromServiceWorker, LoggerLevelEnum.DISPLAY, ...arg);
  }
}
